import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { toast, Bounce } from "react-toastify";
import SEO from "../layout/SEO";
import { FaEye, FaEyeSlash, FaUser, FaLock, FaCode, FaArrowRight, FaCheckCircle } from "react-icons/fa";

const LoginPage = () => {
  const [code, setCode] = useState("");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = (e) => {
    e.preventDefault();

    if (!code || !userId || !password) {
      toast.error("Please fill all fields!", { transition: Bounce });
      return;
    }

    setIsLoading(true);
    
    setTimeout(() => {
      setIsLoading(false);
      toast.success("✅ Login Successful!", { transition: Bounce });
      navigate("/");
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden relative">
      <SEO
        title="Login - SAPEAGLE ERP"
        description="Login to your SAPEAGLE ERP account"
      />

      {/* Animated Background Orbs */}
      <motion.div
        className="absolute w-96 h-96 bg-gradient-to-tr from-blue-500 to-cyan-400 rounded-full blur-3xl opacity-20"
        animate={{ x: [0, 50, -50, 0], y: [0, -50, 50, 0] }}
        transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-80 h-80 bg-gradient-to-bl from-purple-500 to-pink-400 rounded-full blur-3xl opacity-20"
        animate={{ x: [0, -60, 60, 0], y: [0, 60, -60, 0] }}
        transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-72 h-72 bg-gradient-to-r from-green-500 to-teal-400 rounded-full blur-3xl opacity-15"
        animate={{ x: [0, 40, -40, 0], y: [0, -40, 40, 0] }}
        transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
      />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10"
      >
        {/* Main Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white/10 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-10 w-full max-w-md border border-white/20"
          whileHover={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          {/* Decorative Elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            className="absolute -top-12 -left-12"
          >
            <div className="w-24 h-24 bg-gradient-to-tr from-cyan-400 to-blue-500 rounded-full shadow-lg blur-md" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.6, scale: 1 }}
            transition={{ delay: 0.4, type: "spring" }}
            className="absolute -bottom-10 -right-10"
          >
            <div className="w-28 h-28 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl shadow-lg blur-md" />
          </motion.div>

          {/* Logo */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-6"
          >
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              className="bg-gradient-to-tr from-cyan-400 to-blue-600 rounded-2xl p-4 shadow-lg"
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="white" />
                <path d="M12 6a4 4 0 100 8 4 4 0 000-8z" fill="#3B82F6" />
                <path d="M4 18c0-3.314 2.686-6 6-6s6 2.686 6 6" fill="#3B82F6" />
              </svg>
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.h2
            variants={itemVariants}
            className="text-3xl font-bold text-white text-center mb-2"
          >
            Welcome Back
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-gray-400 text-center mb-8"
          >
            Sign in to continue to SAPEAGLE ERP
          </motion.p>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Code Input */}
            <motion.div variants={itemVariants} className="relative">
              <div className={`relative transition-all duration-300 ${focusedField === "code" ? "transform -translate-y-1" : ""}`}>
                <FaCode className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onFocus={() => setFocusedField("code")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Company Code"
                  className={`w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/5 border-2 transition-all duration-300 placeholder-gray-400 text-white focus:outline-none ${
                    focusedField === "code"
                      ? "border-cyan-400 bg-white/10 shadow-lg shadow-cyan-400/20"
                      : "border-white/10 hover:border-white/20"
                  }`}
                />
              </div>
            </motion.div>

            {/* User ID Input */}
            <motion.div variants={itemVariants} className="relative">
              <div className={`relative transition-all duration-300 ${focusedField === "userId" ? "transform -translate-y-1" : ""}`}>
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  onFocus={() => setFocusedField("userId")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="User ID"
                  className={`w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/5 border-2 transition-all duration-300 placeholder-gray-400 text-white focus:outline-none ${
                    focusedField === "userId"
                      ? "border-cyan-400 bg-white/10 shadow-lg shadow-cyan-400/20"
                      : "border-white/10 hover:border-white/20"
                  }`}
                />
              </div>
            </motion.div>

            {/* Password Input */}
            <motion.div variants={itemVariants} className="relative">
              <div className={`relative transition-all duration-300 ${focusedField === "password" ? "transform -translate-y-1" : ""}`}>
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Password"
                  className={`w-full pl-12 pr-14 py-3.5 rounded-xl bg-white/5 border-2 transition-all duration-300 placeholder-gray-400 text-white focus:outline-none ${
                    focusedField === "password"
                      ? "border-cyan-400 bg-white/10 shadow-lg shadow-cyan-400/20"
                      : "border-white/10 hover:border-white/20"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </motion.div>

            {/* Remember Me & Forgot */}
            <motion.div variants={itemVariants} className="flex justify-between items-center text-sm">
              <label className="flex items-center text-gray-300 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe((prev) => !prev)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${rememberMe ? "bg-cyan-500 border-cyan-500" : "border-gray-500 group-hover:border-gray-400"}`}>
                    {rememberMe && <FaCheckCircle className="text-white text-xs" />}
                  </div>
                </div>
                <span className="ml-2">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                Forgot Password?
              </Link>
            </motion.div>

            {/* Login Button */}
            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.02, boxShadow: "0 10px 40px rgba(6,182,212,0.4)" }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                isLoading
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white"
              }`}
            >
              {isLoading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                  className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <>
                  Sign In
                  <FaArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Sign Up Link */}
          <motion.div variants={itemVariants} className="mt-8 text-center text-gray-400">
            Don't have an account?{" "}
            <Link to="/register-page" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors">
              Create Account
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
